
    // IMPORTS
    import { computed, defineComponent } from 'vue'
    import { useEquipmentStore } from '@/store/equipment'
    import { buildLocalization } from '@/util'
    import { RouterLink } from 'vue-router'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MyIcon from '@/components/common/my-icon.vue'

    import { EquipmentMasterData } from '@/types/equipment'

    export default defineComponent({
        name: 'equipmentData',

        components: {
            RouterLink,
            LoaderGuard,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            MyIcon
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const equipmentStore = useEquipmentStore()

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = {}

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const masterData = computed(() => equipmentStore.masterData == null
                ? null
                : new EquipmentMasterData(equipmentStore.masterData)
            )
            const hasLoaded = computed(() => masterData.value != null)
            const deliveryDate = computed(() => masterData.value?.deliveryDate?.toLocaleDateString() ?? '-')
            const anyPartnerData = computed(() => hasAnyPartnerData(masterData.value))
            const soldTo = computed(() => checkNull(masterData.value?.soldToInfo))
            const salesOrderNo = computed(() => checkNull(masterData.value?.salesOrderNo?.toString()))
            const salesOrderRef = computed(() => checkNull(masterData.value?.salesOrderRef?.toString()))
            const contactPerson = computed(() => checkNull(masterData.value?.contactPersonInfo))

            const anyAdminData = computed(() => hasAnyAdminData(masterData.value))
            const productionOrderNo = computed(() => checkNull(masterData.value?.productionOrderNo?.toString()))
            const internalNote = computed(() => getInternalNote(masterData.value))

            const hasBomChanges = computed(() => (equipmentStore.bomDeviations?.length ?? 0) > 0)

            const computedData = {
                masterData,
                hasLoaded,
                deliveryDate,
                anyPartnerData,
                soldTo,
                salesOrderNo,
                salesOrderRef,
                contactPerson,
                anyAdminData,
                productionOrderNo,
                internalNote,
                hasBomChanges
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            const publicMethods = { localize }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function hasAnyPartnerData(data: EquipmentMasterData | null): boolean {
                return (data?.salesOrderNo != null ||
                    data?.salesOrderRef != null ||
                    data?.contactPersonEmail != null ||
                    data?.soldTo != null)
            }

            function hasAnyAdminData(data: EquipmentMasterData | null): boolean {
                return data != null && (data.internalNote != null || data.productionOrderNo != null)
            }

            function getInternalNote(data: EquipmentMasterData | null): string {
                return data?.internalNote == null
                    ? '-'
                    : data.internalNote
                        .replaceAll('\r', '')
                        .replaceAll('\n', '<br>')
            }

            function checkNull(s?: string): string {
                return (s == null)
                    ? '-'
                    : s
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
